import { getValidationMessage } from 'utils/validation';
import {
    date, mixed, number, object, string,
} from 'yup';

import moment from 'moment';
import { TFunction } from 'react-i18next';
import { ZIFD_MIN_WITHDRAWAL_AMOUNT, ZIV_MIN_WITHDRAWAL_AMOUNT } from 'pages/ClientOverview/constants';
import { getMinimumWithdrawalPhaseDurationBasedOnStrategy } from 'pages/ClientOverview/utils/utils';
import { isQRIban } from 'utils';
import { dateValidationRegex, nameValidationRegExp } from './schemaValidationHelpers';

export const getWithdrawerPlanSchema = (
    t: TFunction<'translation', undefined>,
    strategyId: string,
    productSettings: SettingsType | undefined,
    isAzp: boolean,
    isZiv: boolean,
    isZifd: boolean,
    isPk: boolean,
    isAzpD: boolean,
    getAmountWithDecimals,
    isChangeWithdrawal?: boolean,
) => {
    const {
        mandatoryError, nameError, invalidDateFormatError, numberError,
        notBirthDateError, birthDateError,
    } = getValidationMessage();

    const nameValidation = string()
        .required(mandatoryError)
        .test('is-valid', nameError, (val: string) => val !== 'N.A')
        .matches(nameValidationRegExp, {
            message: nameError,
            excludeEmptyString: true,
        });

    const inputNumberValidation = number()
        .typeError(numberError)
        .nullable(false, mandatoryError)
        .required(mandatoryError);

    const stringValueValidation = string()
        .required(mandatoryError)
        .test('is-valid', mandatoryError, (val: string) => val !== 'N/A');

    type mixTypeSelect = string | number | null;

    const selectValidation = mixed<mixTypeSelect>()
        .required(mandatoryError)
        .test(
            'is-valid',
            mandatoryError,
            (val: mixTypeSelect) => val !== 'N/A',
        );

    const commonSchemaModel = {
        frequency: selectValidation,
        iban: isChangeWithdrawal ? string() : string().required(mandatoryError).nullable(false, mandatoryError),
        esr: isChangeWithdrawal ? string() : string()
            .when('iban', {
                is: (iban) => isQRIban(iban),
                then: string()
                    .required(mandatoryError)
                    .test(
                        'no-spaces-length',
                        t('3b.withdrawalPlan.error.esr'),
                        (value) => {
                            if (!value) return false;
                            const cleaned = value.replace(/\s+/g, '');

                            return cleaned.length === 27;
                        },
                    ),
                otherwise: string().notRequired(),
            }),
        firstName: isChangeWithdrawal ? string() : nameValidation,
        lastName: isChangeWithdrawal ? string() : nameValidation,
        bankName: isChangeWithdrawal ? string() : stringValueValidation,
        zipCode: isChangeWithdrawal ? string() : string().matches(/^[0-9]+$/, numberError).nullable(false, mandatoryError).required(mandatoryError),
        city: isChangeWithdrawal ? string() : stringValueValidation,
    };

    const schemaModelPK = {
        amount: inputNumberValidation,
        frequency: selectValidation,
        targetPortfolio: selectValidation,
        startDate: string()
            .required(mandatoryError)
            .matches(dateValidationRegex, notBirthDateError)
            .test(
                'not-empty',
                mandatoryError,
                (val: string) => val !== 'Invalid date',
            )
            .test(
                'is-valid',
                birthDateError,
                (val: string) => {
                    const splitDate = val.split('/');

                    return +splitDate[1] >= 1900;
                },
            ),
    };

    const schemaModelForZifdandZiv = {
        ...commonSchemaModel,
        amount: inputNumberValidation
            .min(
                isZiv ? ZIV_MIN_WITHDRAWAL_AMOUNT : ZIFD_MIN_WITHDRAWAL_AMOUNT,
                t('3b.withdrawalPlan.error.minWithdrawalAmount', {
                    amount: getAmountWithDecimals(isZiv
                        ? ZIV_MIN_WITHDRAWAL_AMOUNT
                        : ZIFD_MIN_WITHDRAWAL_AMOUNT, ''),
                }),
            ),
        startDate: string()
            .required(mandatoryError)
            .matches(dateValidationRegex, notBirthDateError)
            .test(
                'not-empty',
                mandatoryError,
                (val: string) => val !== 'Invalid date',
            )
            .test(
                'is-valid',
                birthDateError,
                (val: string) => {
                    const splitDate = val.split('/');

                    return +splitDate[1] >= 1900;
                },
            ),
    };

    const getRoundedYearsToDate = (stDate) => {
        if (!stDate) {
            return 0;
        }
        const now = moment(); // Current date
        const targetDate = moment(stDate, 'YYYY-MM-DD'); // Convert future date
        const yearsDifference = targetDate.diff(now, 'months'); // Get difference in months

        return Math.round(yearsDifference / 12); // Round to the nearest whole number
    };

    const schemaModelForAzp = {
        ...commonSchemaModel,
        amount: inputNumberValidation
            .min(
                productSettings?.minInvestmentAmount,
                t('3b.withdrawalPlan.error.minInvestmentAmount', {
                    amount: getAmountWithDecimals(productSettings?.minInvestmentAmount, ''),
                }),
            )
            .max(
                productSettings?.maxIvnestmentAmount,
                t('3b.withdrawalPlan.error.maxInvestmentAmount', {
                    amount: getAmountWithDecimals(productSettings?.maxIvnestmentAmount, ''),
                }),
            ),
        withdrawalsStartDate: date()
            .nullable(false, mandatoryError)
            .required(mandatoryError)
            .test(
                'is-valid',
                mandatoryError,
                (val: string | undefined) => val !== 'Invalid date' && val !== undefined,
            )
            .test(
                'is-valid-format',
                invalidDateFormatError,
                (val: boolean | moment.MomentInput) => !val
                    || (val && val !== moment(val as moment.MomentInput).isValid()),
            ),
        duration: inputNumberValidation
            .test(
                'min-duration',
                '3b.withdrawalPlan.error.azp.durationOfWithdrawalPhase',
                function (value) {
                    const { withdrawalsStartDate } = this.parent;
                    const minDuration = isAzpD ? (getMinimumWithdrawalPhaseDurationBasedOnStrategy(+strategyId) || 0) - getRoundedYearsToDate(withdrawalsStartDate) : getMinimumWithdrawalPhaseDurationBasedOnStrategy(+strategyId) || 0;

                    return value >= minDuration;
                },
            ),
    };

    if (isAzp) {
        return object(schemaModelForAzp);
    }
    if (isZiv || isZifd) {
        return object(schemaModelForZifdandZiv);
    }
    if (isPk) {
        return object(schemaModelPK);
    }

    return null;
};
